import React from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';

/**
 * Re-usable activity/phase editor.
 *
 * Each phase row is `{ id, name, weight, progress }`. Weight = % of overall
 * project this activity contributes. Progress = how far this activity has
 * gone (0-100). The overall project progress is the weighted average:
 *
 *   overallProgress = Σ(weight × progress) / Σ(weight)
 *
 * Props:
 *  - phases:        current array
 *  - onChange:      (newArray) => void
 *  - showProgress:  when true (Edit mode), each row also shows a progress slider
 *  - testidPrefix:  prefix for data-testids
 */
export default function PhasesEditor({ phases, onChange, showProgress = false, testidPrefix = 'phase' }) {
  const totalWeight = phases.reduce((s, p) => s + (parseFloat(p.weight) || 0), 0);
  const overallProgress = totalWeight > 0
    ? Math.round(
        phases.reduce((s, p) => s + (parseFloat(p.weight) || 0) * (parseFloat(p.progress) || 0), 0)
        / totalWeight
      )
    : 0;

  const addRow = () => {
    onChange([
      ...phases,
      { id: crypto.randomUUID ? crypto.randomUUID() : String(Math.random()), name: '', weight: '', progress: 0 },
    ]);
  };

  const removeRow = (idx) => {
    onChange(phases.filter((_, i) => i !== idx));
  };

  const updateRow = (idx, key, value) => {
    onChange(phases.map((p, i) => (i === idx ? { ...p, [key]: value } : p)));
  };

  const totalWeightClass = totalWeight === 100
    ? 'bg-green-100 text-green-700'
    : totalWeight > 100
      ? 'bg-rose-100 text-rose-700'
      : 'bg-amber-100 text-amber-700';

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50/50 space-y-2" data-testid={`${testidPrefix}-editor`}>
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">Project Activities & Weights</label>
        <button type="button" onClick={addRow}
                className="text-xs text-[#1a237e] hover:text-[#283593] bg-white border border-[#1a237e]/30 rounded px-2 py-1 flex items-center gap-1"
                data-testid={`${testidPrefix}-add-btn`}>
          <Plus size={12} /> Add Activity
        </button>
      </div>

      {phases.length === 0 && (
        <p className="text-xs text-gray-500 italic py-2 text-center">
          No activities yet. Add activities (e.g. Drawing 10%, Soil cutting 5%) so progress can be tracked per task.
        </p>
      )}

      <div className="space-y-1.5">
        {phases.map((p, idx) => (
          <div key={p.id || idx} className="bg-white border border-gray-200 rounded p-2 space-y-1.5" data-testid={`${testidPrefix}-row-${idx}`}>
            <div className="flex items-center gap-2">
              <GripVertical size={12} className="text-gray-300 shrink-0" />
              <input
                type="text"
                placeholder="Activity name (e.g. Drawing)"
                value={p.name}
                onChange={(e) => updateRow(idx, 'name', e.target.value)}
                className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-indigo-300 focus:border-indigo-400"
                data-testid={`${testidPrefix}-name-${idx}`}
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="0" max="100" step="0.5"
                  placeholder="Weight"
                  value={p.weight}
                  onChange={(e) => updateRow(idx, 'weight', e.target.value)}
                  className="w-20 px-2 py-1 text-sm border border-gray-200 rounded text-right focus:ring-1 focus:ring-indigo-300"
                  data-testid={`${testidPrefix}-weight-${idx}`}
                />
                <span className="text-xs text-gray-500">%</span>
              </div>
              <button type="button" onClick={() => removeRow(idx)}
                      className="p-1 text-rose-500 hover:bg-rose-50 rounded"
                      data-testid={`${testidPrefix}-remove-${idx}`}>
                <Trash2 size={12} />
              </button>
            </div>
            {showProgress && (
              <div className="ml-5 flex items-center gap-2">
                <span className="text-xs text-gray-600 w-16">Progress:</span>
                <input
                  type="range"
                  min="0" max="100" step="5"
                  value={parseFloat(p.progress) || 0}
                  onChange={(e) => updateRow(idx, 'progress', Number(e.target.value))}
                  className="flex-1 accent-[#1a237e]"
                  data-testid={`${testidPrefix}-progress-${idx}`}
                />
                <span className="text-xs font-medium text-gray-700 w-10 text-right">{parseFloat(p.progress) || 0}%</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {phases.length > 0 && (
        <div className="flex items-center justify-between pt-2 border-t border-gray-200 text-xs">
          <span className={`px-2 py-0.5 rounded font-medium ${totalWeightClass}`} data-testid={`${testidPrefix}-total-weight`}>
            Total weight: {totalWeight.toFixed(1)}%
            {totalWeight !== 100 && <span className="ml-1 opacity-70">(should be 100)</span>}
          </span>
          {showProgress && (
            <span className="text-gray-700">
              Auto-computed project progress:{' '}
              <span className="font-semibold text-[#1a237e]" data-testid={`${testidPrefix}-overall-progress`}>{overallProgress}%</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
